import React from 'react';
import { fmtNum } from '../../utils/formatArcaNumbers';

/**
 * Piezas compartidas por las gráficas de proyección (traspaso y plan nuevo), para que las
 * dos cuenten lo mismo y de la misma forma que el funnel.
 */

export interface SeriesName {
	/** Escritorio: el nombre del plan, recortado con «…» si es muy largo. */
	full: string;
	/** Móvil: «Plan de Arca» / «Tu plan actual», que siempre caben. */
	generic: string;
}

/** El nombre del plan en escritorio (recortable) y el genérico en móvil, que siempre cabe. */
export const SeriesLabel: React.FC<{ name: SeriesName; suffix?: string; title?: string }> = ({
	name,
	suffix = '',
	title
}) => (
	<>
		<span data-name="desktop" className="hidden sm:inline truncate min-w-0" title={title}>
			{name.full}
			{suffix}
		</span>
		<span data-name="mobile" className="sm:hidden">
			{name.generic}
			{suffix}
		</span>
	</>
);

type Translate = (key: string) => string;

/** «1 año» / «5 años». */
export const periodLabel = (translate: Translate, years: number): string =>
	years === 1
		? translate('individual.transferProjection.periodOne') || '1 año'
		: (translate('individual.transferProjection.periodMany') || '{years} años').replace(
				'{years}',
				String(years)
			);

/**
 * «1,50% comisión de gestión · 19,28% rentabilidad neta anualizada a 5 años · dic. 2020 – dic. 2025»
 * (guía v3.1: «comisión de gestión», rentabilidades netas y las fechas del dato). Sin comisión o
 * sin fechas, se omite esa parte.
 */
export const legendStats = (
	translate: Translate,
	commission: number | null,
	returnPct: number,
	periodYears: number,
	range: string
): string => {
	const stats = (
		commission === null
			? translate('individual.transferProjection.legendStatsNoFee') ||
				'{returnPct}% rentabilidad neta anualizada a {period}'
			: translate('individual.transferProjection.legendStats') ||
				'{commission}% comisión de gestión · {returnPct}% rentabilidad neta anualizada a {period}'
	)
		.replace('{commission}', fmtNum(commission ?? 0))
		.replace('{returnPct}', fmtNum(returnPct))
		.replace('{period}', periodLabel(translate, periodYears));
	return range ? `${stats} · ${range}` : stats;
};

/**
 * Versión corta para cuando el periodo es el mismo en todas las series: «0,85% comisión de
 * gestión · 9,98% rentabilidad neta». El periodo y las fechas van una sola vez debajo de la
 * gráfica (`returnPeriodLine`), para que la leyenda quepa junto al nombre del plan.
 */
export const legendStatsShort = (
	translate: Translate,
	commission: number | null,
	returnPct: number
): string =>
	(commission === null
		? translate('individual.transferProjection.legendStatsShortNoFee') ||
			'{returnPct}% rentabilidad neta'
		: translate('individual.transferProjection.legendStatsShort') ||
			'{commission}% comisión de gestión · {returnPct}% rentabilidad neta'
	)
		.replace('{commission}', fmtNum(commission ?? 0))
		.replace('{returnPct}', fmtNum(returnPct));

/** «rentabilidad neta anualizada a 3 años (dic. 2022 – dic. 2025)»: sin fechas, sin paréntesis. */
export const periodWithRange = (translate: Translate, years: number, range: string): string =>
	range ? `${periodLabel(translate, years)} (${range})` : periodLabel(translate, years);
